import { useState, useEffect, useRef } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Menu, X, ChevronDown, Clock, Star, Sparkles, Zap, TrendingUp } from 'lucide-react'
import { cn } from '@/lib/utils'

// Dynamic offer system based on current month and festivals
const getCurrentOffer = () => {
  const now = new Date()
  const currentMonth = now.getMonth() + 1 // 1-12
  const currentDay = now.getDate()
  
  // For debugging - log current date info
  console.log(`Current Date: ${currentMonth}/${currentDay}, Month: ${currentMonth}, Day: ${currentDay}`)
  
  // Festival detection with dates (approximate)
  const festivals = [
    { name: "Independence Day", month: 8, dayRange: [15, 25], offer: "Independence Day Special: Up to 26% OFF + Free Career Guidance!", icon: "🇮🇳" },
    { name: "Diwali", month: 10, dayRange: [20, 30], offer: "Diwali Special: Up to 30% OFF + Free Certification!", icon: "🪔" },
    { name: "Holi", month: 3, dayRange: [15, 25], offer: "Holi Special: Up to 25% OFF + Free Career Counseling!", icon: "🎨" },
    { name: "Raksha Bandhan", month: 8, dayRange: [15, 25], offer: "Raksha Bandhan Special: Up to 22% OFF for Siblings!", icon: "🪢" },
    { name: "Republic Day", month: 1, dayRange: [20, 30], offer: "Republic Day Special: Up to 24% OFF!", icon: "🇮🇳" },
    { name: "New Year", month: 1, dayRange: [1, 15], offer: "New Year Special: Up to 28% OFF + Free Placement Support!", icon: "🎊" },
    { name: "Christmas", month: 12, dayRange: [20, 31], offer: "Christmas Special: Up to 27% OFF + Free Study Material!", icon: "🎄" },
    { name: "Eid", month: 4, dayRange: [10, 20], offer: "Eid Special: Up to 23% OFF + Free Mock Interviews!", icon: "🌙" },
    { name: "Ganesh Chaturthi", month: 9, dayRange: [15, 25], offer: "Ganesh Chaturthi Special: Up to 25% OFF!", icon: "🕉️" },
    { name: "Navratri", month: 10, dayRange: [1, 15], offer: "Navratri Special: Up to 26% OFF + Free Project Support!", icon: "🕉️" },
    { name: "Makar Sankranti", month: 1, dayRange: [10, 20], offer: "Makar Sankranti Special: Up to 24% OFF!", icon: "🪁" },
    { name: "Guru Nanak Jayanti", month: 11, dayRange: [15, 25], offer: "Guru Nanak Jayanti Special: Up to 22% OFF!", icon: "☬" }
  ]
  
  // Check for festivals first
  for (const festival of festivals) {
    if (festival.month === currentMonth && currentDay >= festival.dayRange[0] && currentDay <= festival.dayRange[1]) {
      console.log(`Festival detected: ${festival.name}`)
      return {
        message: festival.offer,
        icon: festival.icon,
        type: 'festival',
        festivalName: festival.name
      }
    }
  }
  
  // Monthly offers based on current month
  const monthlyOffers = {
    1: { message: "New Year Special: Up to 28% OFF + Free Placement Support!", icon: "🎊", type: 'monthly', month: "January" },
    2: { message: "Valentine's Special: Up to 25% OFF for Couples!", icon: "💝", type: 'monthly', month: "February" },
    3: { message: "Spring Special: Up to 26% OFF + Free Career Guidance!", icon: "🌸", type: 'monthly', month: "March" },
    4: { message: "Summer Special: Up to 24% OFF + Free Study Material!", icon: "☀️", type: 'monthly', month: "April" },
    5: { message: "May Special: Up to 27% OFF + Free Mock Interviews!", icon: "🌺", type: 'monthly', month: "May" },
    6: { message: "Monsoon Special: Up to 25% OFF + Free Project Support!", icon: "🌧️", type: 'monthly', month: "June" },
    7: { message: "July Special: Up to 26% OFF + Free Certification!", icon: "🌻", type: 'monthly', month: "July" },
    8: { message: "August Special: Up to 28% OFF + Free Career Counseling!", icon: "🌿", type: 'monthly', month: "August" },
    9: { message: "September Special: Up to 25% OFF + Free Placement Support!", icon: "🍂", type: 'monthly', month: "September" },
    10: { message: "October Special: Up to 27% OFF + Free Study Material!", icon: "🎃", type: 'monthly', month: "October" },
    11: { message: "November Special: Up to 26% OFF + Free Mock Interviews!", icon: "🍁", type: 'monthly', month: "November" },
    12: { message: "December Special: Up to 29% OFF + Free Project Support!", icon: "❄️", type: 'monthly', month: "December" }
  }
  
  // Return monthly offer for current month
  const monthlyOffer = monthlyOffers[currentMonth as keyof typeof monthlyOffers]
  if (monthlyOffer) {
    console.log(`Monthly offer detected: ${monthlyOffer.month}`)
    return monthlyOffer
  }
  
  // Fallback
  console.log("No specific offer found, showing default")
  return {
    message: "Flash Sale: Up to 25% OFF on All Courses!",
    icon: "🎉",
    type: 'default',
    month: "Special"
  }
}

const navigation = [
  { name: 'Success Stories', href: '/success-stories' },
  { name: 'About', href: '/about' },
  { name: 'Contact', href: '/contact' },
  { name: 'Hire from Us', href: 'https://hire.cloudblitz.in'}
]

const coursesDropdown = [
  { 
    name: 'AI & Data Analytics with Cloud DevOps', 
    href: '/courses/ai-data-analytics-cloud-devops',
    duration: '6 Months',
    price: '₹95,000',
    originalPrice: '₹1,25,000',
    discount: '25% OFF',
    badge: 'New',
    image: '/courses/Expert in Data Science and Analytics with AI.webp'
  },
  { 
    name: 'Cloud DevOps Engineering Course with AI', 
    href: '/courses/cloud-devops-engineering-ai',
    duration: '6 Months',
    price: '₹90,000',
    originalPrice: '₹1,20,000',
    discount: '25% OFF',
    badge: 'Bestseller',
    image: '/courses/Cloud DevOps Engineering Course with AI.webp'
  },
  { 
    name: 'Integrated Full Stack with Cloud DevOps', 
    href: '/courses/integrated-fullstack-cloud-devops-ai',
    duration: '8 Months',
    price: '₹90,000',
    originalPrice: '₹1,15,000',
    discount: '22% OFF',
    badge: 'Popular',
    image: '/courses/Integrated Full Stack with Cloud DevOps Engineering course.webp'
  },
  { 
    name: 'Expert in Data Science and Analytics with AI', 
    href: '/courses/expert-data-science-analytics-ai',
    duration: '8 Months',
    price: '₹90,000',
    originalPrice: '₹1,25,000',
    discount: '28% OFF',
    badge: 'New',
    image: '/courses/Expert in Data Science and Analytics with AI.webp'
  },
  { 
    name: 'Expert in Digital Marketing with AI', 
    href: '/courses/expert-digital-marketing-ai',
    duration: '3 Months',
    price: '₹40,000',
    originalPrice: '₹45,000',
    discount: '22% OFF',
    badge: 'Trending',
    image: '/courses/Expert in Digital Marketing with AI.webp'
  },
  { 
    name: 'Ultimate Cloud Architect: AWS, Azure & GCP', 
    href: '/courses/ultimate-cloud-architect',
    duration: '4 Months',
    price: '₹60,000',
    originalPrice: '₹80,000',
    discount: '25% OFF',
    badge: 'Hot',
    image: '/courses/Ultimate Cloud Architect_ AWS, Azure & GCP Certification Training.webp'
  }
]

export default function StickyHeader() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [coursesOpen, setCoursesOpen] = useState(false)
  const [showOffer, setShowOffer] = useState(true)
  const [isScrolled, setIsScrolled] = useState(false)
  const location = useLocation()
  const dropdownRef = useRef<HTMLDivElement>(null)
  
  // Get current dynamic offer
  const currentOffer = getCurrentOffer()

  // Handle scroll detection
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY
      setIsScrolled(scrollTop > 50)
    }

    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setCoursesOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  return (
    <>
      {/* Top Offer Bar */}
      {showOffer && !isScrolled && (
        <div className="bg-gradient-to-r from-orange-500 via-red-500 to-purple-600 text-white relative overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(255,255,255,0.1),transparent_50%)]"></div>
          </div>
          <div className="relative mx-auto max-w-7xl px-6 py-3 lg:px-8">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="flex items-center space-x-2">
                  <Sparkles className="h-4 w-4 animate-pulse" />
                  <span className="text-sm font-semibold">{currentOffer.icon} {currentOffer.message}</span>
                </div>
                <div className="hidden md:flex items-center space-x-2 text-xs">
                  <Clock className="h-3 w-3" />
                  <span>
                    {currentOffer.type === 'festival' 
                      ? `${(currentOffer as any).festivalName} Special Offer` 
                      : currentOffer.type === 'monthly' 
                        ? `${(currentOffer as any).month} Special Offer`
                        : 'Limited Time Offer'
                    }
                  </span>
                </div>
              </div>
              <div className="flex items-center space-x-4">
                <div className="hidden md:flex items-center space-x-2 text-xs">
                  <Star className="h-3 w-3 text-yellow-300" />
                  <span>4.8/5 Rating</span>
                </div>
                <button
                  onClick={() => setShowOffer(false)}
                  className="text-white/80 hover:text-white transition-colors"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main Header */}
      <header className="sticky top-0 z-50 py-4">
        <div className="mx-auto max-w-7xl px-4 lg:px-8">
          {/* Pill-shaped navigation container */}
          <div className="bg-transparent backdrop-blur-sm rounded-full border border-gray-300 shadow-lg">
            <nav className="flex items-center justify-between px-6 py-3" aria-label="Global">
              {/* Logo */}
              <div className="flex items-center">
                <Link to="/" className="flex items-center group">
                  <div className="relative">
                    <img 
                      src="/logo.webp" 
                      alt="CloudBlitz"
                      loading="eager"
                      decoding="async"
                      width="200"
                      height="60" 
                      className="h-8 w-auto lg:h-10 transition-transform duration-300 group-hover:scale-105"
                    />
                    <div className="absolute -inset-2 bg-gradient-to-r from-orange-500/20 to-purple-500/20 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 blur-xl"></div>
                  </div>
                </Link>
              </div>
              
              {/* Desktop Navigation - Center aligned */}
              <div className="hidden lg:flex lg:items-center lg:space-x-8">
                {navigation.map((item) => (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={cn(
                      "relative text-sm font-medium leading-6 transition-all duration-200 group px-3 py-2 rounded-lg",
                      location.pathname === item.href
                        ? "text-orange-500 bg-white/30"
                        : "text-gray-800 hover:text-orange-600 hover:bg-white/20"
                    )}
                  >
                    {item.name}
                    <span className={cn(
                      "absolute -bottom-1 left-1/2 w-0 h-0.5 bg-gradient-to-r from-orange-500 to-purple-500 transition-all duration-300 group-hover:w-full transform -translate-x-1/2",
                      location.pathname === item.href && "w-full"
                    )}></span>
                  </Link>
                ))}
                
                {/* Courses Dropdown */}
                <div className="relative" ref={dropdownRef}>
                  <button
                    onClick={() => setCoursesOpen(!coursesOpen)}
                    className={cn(
                      "flex items-center text-sm font-medium leading-6 transition-all duration-200 group px-3 py-2 rounded-lg",
                      coursesOpen
                        ? "text-orange-500 bg-white/30"
                        : "text-gray-800 hover:text-orange-600 hover:bg-white/20"
                    )}
                  >
                    Courses
                    <ChevronDown className={cn("ml-1 h-4 w-4 transition-transform duration-200", coursesOpen && "rotate-180")} />
                    <span className="absolute -bottom-1 left-1/2 w-0 h-0.5 bg-gradient-to-r from-orange-500 to-purple-500 transition-all duration-300 group-hover:w-full transform -translate-x-1/2"></span>
                  </button>
                  
                  {coursesOpen && (
                    <div className="absolute top-full left-1/2 transform -translate-x-1/2 mt-3 w-96 bg-white/95 backdrop-blur-xl rounded-2xl shadow-2xl border border-white/20 py-4 z-50 animate-in slide-in-from-top-2 duration-200">
                      <div className="px-4 pb-3 border-b border-gray-100">
                        <div className="flex items-center space-x-2 text-sm font-semibold text-gray-900">
                          <TrendingUp className="h-4 w-4 text-orange-500" />
                          <span>Popular Courses</span>
                        </div>
                      </div>
                      {coursesDropdown.map((course) => (
                        <Link
                          key={course.name}
                          to={course.href}
                          className="block px-4 py-4 text-sm text-gray-700 hover:bg-gradient-to-r hover:from-orange-50 hover:to-purple-50 hover:text-orange-600 transition-all duration-200 border-b border-gray-50 last:border-b-0 group"
                          onClick={() => setCoursesOpen(false)}
                        >
                          <div className="flex items-start space-x-3 mb-2">
                            {/* Course Thumbnail */}
                            {course.image && (
                              <div className="flex-shrink-0 w-12 h-8 rounded-lg overflow-hidden bg-gradient-to-br from-orange-500 to-purple-600">
                                <img 
                                  src={course.image} 
                                  alt={course.name}
                                  className="w-full h-full object-cover"
                                />
                              </div>
                            )}
                            <div className="flex-1 min-w-0">
                              <div className="font-semibold text-gray-900 group-hover:text-orange-600 transition-colors line-clamp-2">
                                {course.name}
                              </div>
                            </div>
                            <div className="flex items-center space-x-2 ml-2 flex-shrink-0">
                              {course.badge === 'Bestseller' && (
                                <span className="px-2 py-1 text-xs font-semibold bg-gradient-to-r from-yellow-400 to-orange-500 text-white rounded-full">
                                  {course.badge}
                                </span>
                              )}
                              {course.badge === 'Popular' && (
                                <span className="px-2 py-1 text-xs font-semibold bg-gradient-to-r from-pink-400 to-rose-500 text-white rounded-full">
                                  {course.badge}
                                </span>
                              )}
                              {course.badge === 'New' && (
                                <span className="px-2 py-1 text-xs font-semibold bg-gradient-to-r from-green-400 to-emerald-500 text-white rounded-full">
                                  {course.badge}
                                </span>
                              )}
                              {course.badge === 'Trending' && (
                                <span className="px-2 py-1 text-xs font-semibold bg-gradient-to-r from-purple-400 to-indigo-500 text-white rounded-full">
                                  {course.badge}
                                </span>
                              )}
                              {course.badge === 'Hot' && (
                                <span className="px-2 py-1 text-xs font-semibold bg-gradient-to-r from-red-400 to-pink-500 text-white rounded-full">
                                  {course.badge}
                                </span>
                              )}
                              {!['Bestseller', 'Popular', 'New', 'Trending', 'Hot'].includes(course.badge) && course.badge && (
                                <span className="px-2 py-1 text-xs font-semibold bg-gradient-to-r from-green-400 to-emerald-500 text-white rounded-full">
                                  {course.badge}
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-4 text-xs text-gray-500">
                              <span className="flex items-center">
                                <Clock className="h-3 w-3 mr-1" />
                                {course.duration}
                              </span>
                            </div>
                            <div className="text-right">
                              <div className="flex items-center space-x-2">
                              </div>
                              <div className="text-xs font-semibold text-green-600">{course.discount}</div>
                            </div>
                          </div>
                        </Link>
                      ))}
                      <div className="px-4 pt-3 border-t border-gray-100">
                        <Link
                          to="/courses"
                          className="flex items-center justify-center w-full py-2 text-sm font-semibold text-orange-600 hover:text-orange-700 transition-colors"
                          onClick={() => setCoursesOpen(false)}
                        >
                          View All Courses
                          <ChevronDown className="ml-1 h-4 w-4 rotate-[-90deg]" />
                        </Link>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Right side - Login button and WhatsApp */}
              <div className="hidden lg:flex lg:items-center space-x-3">
                <Button 
                  variant="outline" 
                  className="border-gray-300 hover:border-orange-500 hover:bg-orange-500/20 transition-all duration-200 rounded-full text-gray-800"
                  onClick={() => window.open('https://edublitz.edmingle.com/login', '_blank', 'noopener,noreferrer')}
                >
                  <div className="flex items-center space-x-2">
                    <Zap className="h-4 w-4" />
                    <span>Login</span>
                  </div>
                </Button>
                <Button 
                  variant="outline" 
                  className="border-gray-300 hover:border-green-500 hover:bg-green-500/20 transition-all duration-200 rounded-full group text-gray-800"
                  onClick={() => {
                    const phoneNumber = '+919834887259'
                    const message = 'Hi! I\'m interested in learning more about CloudBlitz courses. Can you help me?'
                    const whatsappUrl = `https://wa.me/${phoneNumber.replace('+', '')}?text=${encodeURIComponent(message)}`
                    window.open(whatsappUrl, '_blank')
                  }}
                >
                  <div className="flex items-center space-x-2">
                    <div className="relative">
                      <svg className="h-4 w-4 text-green-600" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488"/>
                      </svg>
                      <div className="absolute -top-1 -right-1 w-2 h-2 bg-green-500 rounded-full animate-pulse group-hover:scale-150 transition-transform"></div>
                    </div>
                    <span className="text-green-600 group-hover:text-green-700">Chat</span>
                  </div>
                </Button>
              </div>
              
              {/* Mobile menu button */}
              <div className="flex lg:hidden">
                <button
                  type="button"
                  className="-m-2.5 inline-flex items-center justify-center rounded-lg p-2.5 text-gray-800 hover:bg-gray-100 transition-colors"
                  onClick={() => setMobileMenuOpen(true)}
                >
                  <span className="sr-only">Open main menu</span>
                  <Menu className="h-6 w-6" aria-hidden="true" />
                </button>
              </div>
            </nav>
          </div>
        </div>
        
        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden">
            <div className="fixed inset-0 z-50 bg-black/20 backdrop-blur-sm" />
            <div className="fixed inset-y-0 right-0 z-50 w-full overflow-y-auto bg-white/95 backdrop-blur-xl px-6 py-6 sm:max-w-sm sm:ring-1 sm:ring-gray-900/10">
              <div className="flex items-center justify-between mb-8">
                <Link to="/" className="-m-1.5 p-1.5 flex items-center">
                  <img 
                    src="/logo.webp" 
                    alt="CloudBlitz" 
                    className="h-8 w-auto" 
                  />
                </Link>
                <button
                  type="button"
                  className="-m-2.5 rounded-lg p-2.5 text-gray-700 hover:bg-gray-100 transition-colors"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <span className="sr-only">Close menu</span>
                  <X className="h-6 w-6" aria-hidden="true" />
                </button>
              </div>
              
              {/* Mobile Navigation */}
              <div className="space-y-6">
                {navigation.map((item) => (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={cn(
                      "block text-lg font-medium transition-colors duration-200",
                      location.pathname === item.href
                        ? "text-orange-600"
                        : "text-gray-900 hover:text-orange-600"
                    )}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {item.name}
                  </Link>
                ))}
                
                {/* Mobile Courses Dropdown */}
                <div className="space-y-4">
                  <div className="text-lg font-medium text-gray-900">Courses</div>
                  {coursesDropdown.map((course) => (
                    <Link
                      key={course.name}
                      to={course.href}
                      className="block p-4 bg-gray-50 rounded-xl hover:bg-orange-50 transition-colors"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className="font-semibold text-gray-900 text-sm line-clamp-2">
                          {course.name}
                        </div>
                        <span className="px-2 py-1 text-xs font-semibold bg-orange-100 text-orange-700 rounded-full ml-2">
                          {course.badge}
                        </span>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-500">{course.duration}</span>
                        <div className="text-right">
                          <div className="flex items-center space-x-2">
                          </div>
                          <div className="text-xs font-semibold text-green-600">{course.discount}</div>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
                
                {/* Mobile CTA Buttons */}
                <div className="space-y-3 pt-6 border-t border-gray-200">
                  <Button 
                    variant="outline" 
                    className="w-full"
                    onClick={() => {
                      window.open('https://edublitz.edmingle.com/login', '_blank', 'noopener,noreferrer')
                      setMobileMenuOpen(false)
                    }}
                  >
                    Login
                  </Button>
                  <Button 
                    variant="outline" 
                    className="w-full border-green-200 hover:border-green-300 hover:bg-green-50 group"
                    onClick={() => {
                      const phoneNumber = '+919834887259'
                      const message = 'Hi! I\'m interested in learning more about CloudBlitz courses. Can you help me?'
                      const whatsappUrl = `https://wa.me/${phoneNumber.replace('+', '')}?text=${encodeURIComponent(message)}`
                      window.open(whatsappUrl, '_blank')
                      setMobileMenuOpen(false)
                    }}
                  >
                    <div className="flex items-center justify-center space-x-2">
                      <svg className="h-4 w-4 text-green-600" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488"/>
                      </svg>
                      <span className="text-green-600 group-hover:text-green-700">WhatsApp Chat</span>
                    </div>
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </header>
    </>
  )
} 